import { Bot, Sparkles, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import type { AgentRunSummary as AgentRunSummaryType } from '@/types/agent'
import { useAgentLabels } from '@/hooks'

interface Props {
  summary: AgentRunSummaryType
}

export function AgentRunSummaryBanner({ summary }: Props) {
  const [expanded, setExpanded] = useState(false)
  const { modelLabel, effortLabel } = useAgentLabels()

  if (summary.totalAgentNodes === 0) return null

  const chevronCls = 'h-4 w-4 shrink-0 text-info-foreground'
  const hasDetails = summary.overrideClusters.length > 0

  const heading = (
    <>
      <Bot className="h-4 w-4 shrink-0 text-info-foreground" aria-hidden />

      <span className="font-medium">
        {summary.totalAgentNodes} AI agent {summary.totalAgentNodes === 1 ? 'node' : 'nodes'}
      </span>

      {summary.overriddenCount > 0 && (
        <span
          className="ml-1 rounded bg-warning px-1.5 text-xs font-medium text-warning-foreground"
          title="The author-preferred model was superseded by the selection made at trigger time."
        >
          {summary.overriddenCount} overridden
        </span>
      )}
    </>
  )

  return (
    <div className="rounded-md border border-info-foreground bg-info text-sm text-info-foreground" data-testid="agent-run-summary">
      {hasDetails ? (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-info/60"
          onClick={() => setExpanded(p => !p)}
          aria-expanded={expanded}
          data-testid="agent-run-summary-toggle"
        >
          {heading}

          <span className="ml-auto">
            {expanded
              ? <ChevronDown className={chevronCls} aria-hidden />
              : <ChevronRight className={chevronCls} aria-hidden />}
          </span>
        </button>
      ) : (
        <div className="flex w-full items-center gap-2 px-3 py-2">
          {heading}
        </div>
      )}

      {expanded && hasDetails && (
        <div className="border-t border-info-foreground px-3 py-2">
          <ul className="space-y-1">
            {summary.overrideClusters.map(c => (
              <li key={`${c.model}-${c.effort}`} className="flex items-center gap-2 text-xs">
                <Sparkles className="h-3 w-3 shrink-0 text-info-foreground" aria-hidden />
                <span>
                  <strong>{modelLabel(c.model)}</strong>{' · '}
                  {effortLabel(c.effort)} effort
                </span>
                <span className="text-info-foreground/70">
                  — {c.count} {c.count === 1 ? 'node' : 'nodes'}
                  {c.yamlPinnedCount != null && c.yamlPinnedCount > 0 && (
                    <>, {c.yamlPinnedCount} superseding the author-preferred model</>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
